import { Table, Button, Modal, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Eye, MessageSquare } from 'lucide-react'
import React, { useState } from 'react'
import { useQuery } from 'react-query'

import { publicChatLogsApi, type PublicChatLogSummary } from '../services/api'

const PublicChatLogsPage: React.FC = () => {
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [sessionId, setSessionId] = useState<string | null>(null)

  const { data, isLoading } = useQuery(
    ['public-chat-logs', page, limit],
    () => publicChatLogsApi.list({ page, limit }),
    { keepPreviousData: true }
  )

  const { data: detail, isFetching: detailLoading } = useQuery(
    ['public-chat-log', sessionId ?? ''],
    () => publicChatLogsApi.get(sessionId as string),
    { enabled: !!sessionId }
  )

  const columns: ColumnsType<PublicChatLogSummary> = [
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: 'Visitor',
      key: 'visitor',
      render: (_, row) => row.visitorName || row.visitorEmail || 'Unknown',
    },
    {
      title: 'Email',
      dataIndex: 'visitorEmail',
      key: 'visitorEmail',
      render: (value: string | null) => value || '—',
    },
    {
      title: 'Messages',
      dataIndex: 'messageCount',
      key: 'messageCount',
      width: 110,
    },
    {
      title: 'Last question',
      dataIndex: 'preview',
      key: 'preview',
      ellipsis: true,
      render: (value: string | null) => value || 'Opened the chat',
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, row) => (
        <Button type="link" size="small" icon={<Eye className="h-4 w-4" />} onClick={() => setSessionId(row.sessionId)}>
          View
        </Button>
      ),
    },
  ]

  const transcript = detail?.data

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
          <MessageSquare className="h-8 w-8 text-primary-600" />
          Public chats
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Conversations from the Talk to the Developer chat on the public site. New chats appear after the visitor opens the widget.
        </p>
      </div>

      <Table
        rowKey="sessionId"
        loading={isLoading}
        columns={columns}
        dataSource={data?.logs ?? []}
        pagination={{
          current: page,
          pageSize: limit,
          total: data?.total ?? 0,
          onChange: setPage,
          showSizeChanger: false,
        }}
      />

      <Modal
        title="Chat transcript"
        open={!!sessionId}
        onCancel={() => setSessionId(null)}
        footer={null}
        width={720}
      >
        {detailLoading || !transcript ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-500">
              {transcript.visitor.name || 'Unknown visitor'}
              {transcript.visitor.email ? ` · ${transcript.visitor.email}` : ''}
              {transcript.visitor.phone ? ` · ${transcript.visitor.phone}` : ''}
              {transcript.visitor.businessType ? ` · ${transcript.visitor.businessType}` : ''}
            </div>
            <div className="max-h-[60vh] space-y-3 overflow-y-auto">
              {transcript.messages.map((message, index) => (
                <div key={`${message.timestamp}-${index}`}>
                  <Tag color={message.role === 'user' ? 'blue' : 'default'}>
                    {message.role === 'user' ? 'Visitor' : 'Assistant'}
                  </Tag>
                  <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                    {message.content}
                  </Typography.Paragraph>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default PublicChatLogsPage
